export type OrbState = 'idle' | 'listening' | 'speaking' | 'thinking';

interface Particle {
  angle: number;
  radius: number;
  speed: number;
  size: number;
  life: number;
  hue: number;
}

interface Palette {
  core: string;
  glow: string;
  rim: string;
  accent: string;
  ring: string;
}

const PALETTES: Record<OrbState, Palette> = {
  idle: {
    core: 'rgba(180, 200, 255, 0.9)',
    glow: 'rgba(120, 150, 220, 0.55)',
    rim: 'rgba(80, 120, 200, 0.6)',
    accent: 'rgba(200, 220, 255, 0.9)',
    ring: 'rgba(100, 140, 220, 0.35)',
  },
  listening: {
    core: 'rgba(150, 230, 255, 0.95)',
    glow: 'rgba(70, 180, 230, 0.6)',
    rim: 'rgba(30, 160, 220, 0.7)',
    accent: 'rgba(180, 240, 255, 0.95)',
    ring: 'rgba(80, 200, 255, 0.4)',
  },
  speaking: {
    core: 'rgba(255, 220, 160, 1.0)',
    glow: 'rgba(240, 180, 80, 0.7)',
    rim: 'rgba(230, 150, 70, 0.75)',
    accent: 'rgba(255, 235, 180, 1.0)',
    ring: 'rgba(240, 190, 100, 0.5)',
  },
  thinking: {
    core: 'rgba(200, 170, 240, 0.95)',
    glow: 'rgba(150, 110, 210, 0.6)',
    rim: 'rgba(130, 90, 200, 0.7)',
    accent: 'rgba(230, 200, 255, 0.95)',
    ring: 'rgba(160, 120, 220, 0.4)',
  },
};

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

// Canvas is 170% of visual container — scale baseR so orb body stays same apparent size.
export const BASE_R_FACTOR = 0.13;

function lerpColor(a: Palette, b: Palette, t: number): Palette {
  const parse = (c: string) => {
    const m = c.match(/rgba?\(([^)]+)\)/);
    if (!m) return [255, 255, 255, 1];
    return (m[1] ?? '').split(',').map((v) => parseFloat(v.trim()));
  };
  const mix = (ca: string, cb: string) => {
    const A = parse(ca);
    const B = parse(cb);
    const r = Math.round(lerp(A[0] ?? 0, B[0] ?? 0, t));
    const g = Math.round(lerp(A[1] ?? 0, B[1] ?? 0, t));
    const bl = Math.round(lerp(A[2] ?? 0, B[2] ?? 0, t));
    const al = lerp(A[3] ?? 1, B[3] ?? 1, t).toFixed(3);
    return `rgba(${r}, ${g}, ${bl}, ${al})`;
  };
  return {
    core: mix(a.core, b.core),
    glow: mix(a.glow, b.glow),
    rim: mix(a.rim, b.rim),
    accent: mix(a.accent, b.accent),
    ring: mix(a.ring, b.ring),
  };
}

export class MerlinOrb {
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private dpr = 1;
  private width = 0;
  private height = 0;
  private rafId?: number;

  private state: OrbState = 'idle';
  private prevState: OrbState = 'idle';
  private stateBlend = 1;
  private currentPalette: Palette = PALETTES.idle;

  private analyser?: AnalyserNode;
  private analyserBuf?: Float32Array<ArrayBuffer>;
  private amplitude = 0;
  private smoothAmp = 0;
  private time = 0;
  private lastFrameTime = performance.now();

  private particles: Particle[] = [];
  private readonly resizeListener = () => this.resize();
  private resizeObserver?: ResizeObserver;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) throw new Error('2D canvas context unavailable');
    this.ctx = ctx;
    this.resize();
    window.addEventListener('resize', this.resizeListener);
    // The intake two-column layout can resize the orb's container (grid
    // column widths, rail panels appearing) without a window resize event.
    // Guarded for jsdom/older browsers where ResizeObserver is absent.
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.resize());
      this.resizeObserver.observe(canvas.parentElement ?? canvas);
    }
    this.seedParticles();
  }

  setState(state: OrbState) {
    if (state === this.state) return;
    this.prevState = this.state;
    this.state = state;
    this.stateBlend = 0;
  }

  setAnalyser(analyser: AnalyserNode | undefined) {
    this.analyser = analyser;
    if (analyser) {
      analyser.fftSize = 512;
      this.analyserBuf = new Float32Array(new ArrayBuffer(analyser.fftSize * 4));
    } else {
      this.analyserBuf = undefined;
    }
  }

  start() {
    if (this.rafId != null) return;
    this.lastFrameTime = performance.now();
    const loop = () => {
      this.rafId = requestAnimationFrame(loop);
      const now = performance.now();
      const dt = Math.min(0.05, (now - this.lastFrameTime) / 1000);
      this.lastFrameTime = now;
      this.tick(dt);
      this.render();
    };
    this.rafId = requestAnimationFrame(loop);
  }

  stop() {
    if (this.rafId != null) cancelAnimationFrame(this.rafId);
    this.rafId = undefined;
  }

  destroy() {
    this.stop();
    this.setAnalyser(undefined);
    window.removeEventListener('resize', this.resizeListener);
    this.resizeObserver?.disconnect();
  }

  private resize() {
    this.dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
    const rect = this.canvas.getBoundingClientRect();
    const cssW = rect.width || 360;
    const cssH = rect.height || 360;
    this.canvas.width = Math.round(cssW * this.dpr);
    this.canvas.height = Math.round(cssH * this.dpr);
    this.width = cssW;
    this.height = cssH;
  }

  private seedParticles() {
    this.particles = [];
    for (let i = 0; i < 60; i++) {
      this.particles.push({
        angle: Math.random() * Math.PI * 2,
        radius: 110 + Math.random() * 90,
        speed: 0.15 + Math.random() * 0.4,
        size: 0.8 + Math.random() * 1.8,
        life: Math.random(),
        hue: Math.random(),
      });
    }
  }

  private tick(dt: number) {
    this.time += dt;

    if (this.analyser && this.analyserBuf) {
      this.analyser.getFloatTimeDomainData(this.analyserBuf);
      let sum = 0;
      for (let i = 0; i < this.analyserBuf.length; i++) {
        const v = this.analyserBuf[i] ?? 0;
        sum += v * v;
      }
      const rms = Math.sqrt(sum / this.analyserBuf.length);
      this.amplitude = Math.min(1, rms * 4.5);
    } else {
      this.amplitude = 0;
    }

    const target =
      this.state === 'speaking'
        ? this.amplitude
        : this.state === 'listening'
          ? 0.25 + Math.sin(this.time * 2.2) * 0.08
          : 0.12 + Math.sin(this.time * 0.8) * 0.05;

    const follow = this.state === 'speaking' ? 0.2 : 0.08;
    this.smoothAmp = lerp(this.smoothAmp, target, follow);

    if (this.stateBlend < 1) {
      this.stateBlend = Math.min(1, this.stateBlend + dt * 2.2);
    }
    this.currentPalette = lerpColor(
      PALETTES[this.prevState],
      PALETTES[this.state],
      this.stateBlend,
    );

    for (const p of this.particles) {
      p.angle += p.speed * dt * (0.5 + this.smoothAmp * 2.5);
      p.life += dt * 0.35;
      if (p.life > 1) p.life -= 1;
    }
  }

  private render() {
    const { ctx, width, height, dpr } = this;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    const cx = width / 2;
    const cy = height / 2;
    const baseR = Math.min(width, height) * BASE_R_FACTOR;
    const amp = this.smoothAmp;
    const pal = this.currentPalette;

    // Outer aura — giant soft halo
    const auraR = baseR * (2.4 + amp * 1.3);
    const aura = ctx.createRadialGradient(cx, cy, baseR * 0.6, cx, cy, auraR);
    aura.addColorStop(0, pal.glow);
    aura.addColorStop(0.45, withAlpha(pal.glow, 0.22));
    aura.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.globalCompositeOperation = 'lighter';
    ctx.fillStyle = aura;
    ctx.beginPath();
    ctx.arc(cx, cy, auraR, 0, Math.PI * 2);
    ctx.fill();

    // Energy rings — rotating arcs
    this.drawEnergyRing(cx, cy, baseR * 1.55, 2.6, this.time * 0.45, pal.ring, amp);
    this.drawEnergyRing(cx, cy, baseR * 1.8, 1.4, -this.time * 0.3, pal.ring, amp * 0.7);
    this.drawEnergyRing(cx, cy, baseR * 2.1, 0.9, this.time * 0.18, pal.ring, amp * 0.5);

    // Particles orbiting
    for (const p of this.particles) {
      const r = p.radius + Math.sin(this.time + p.angle) * 6 + amp * 10;
      const px = cx + Math.cos(p.angle) * r;
      const py = cy + Math.sin(p.angle) * r * 0.96;
      const alpha = 0.35 + Math.sin(p.life * Math.PI * 2) * 0.3 + amp * 0.25;
      ctx.fillStyle = withAlpha(pal.accent, Math.max(0, Math.min(1, alpha)));
      ctx.beginPath();
      ctx.arc(px, py, p.size * (0.8 + amp * 0.8), 0, Math.PI * 2);
      ctx.fill();
    }

    // Core orb — multi-layer radial. Drawn with normal compositing so it
    // doesn't add to the aura beneath and clamp to white at center.
    const coreR = baseR * (1 + amp * 0.12);
    const rim = ctx.createRadialGradient(cx, cy, coreR * 0.2, cx, cy, coreR);
    rim.addColorStop(0, withAlpha(pal.core, 0.7));
    rim.addColorStop(0.55, withAlpha(pal.core, 0.45));
    rim.addColorStop(0.9, withAlpha(pal.rim, 0.4));
    rim.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = rim;
    ctx.beginPath();
    ctx.arc(cx, cy, coreR, 0, Math.PI * 2);
    ctx.fill();

    // Inner sheen — offset highlight, gives depth. Subtle so the offset
    // hotspot reads as form, not a blown-out specular.
    const sheenX = cx - coreR * 0.25;
    const sheenY = cy - coreR * 0.35;
    const sheen = ctx.createRadialGradient(
      sheenX,
      sheenY,
      0,
      sheenX,
      sheenY,
      coreR * 0.75,
    );
    sheen.addColorStop(0, withAlpha(pal.accent, 0.22));
    sheen.addColorStop(0.6, withAlpha(pal.accent, 0.05));
    sheen.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = sheen;
    ctx.beginPath();
    ctx.arc(cx, cy, coreR, 0, Math.PI * 2);
    ctx.fill();

    // Thin crystalline outline
    ctx.globalCompositeOperation = 'source-over';
    ctx.strokeStyle = withAlpha(pal.accent, 0.35 + amp * 0.35);
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.arc(cx, cy, coreR + 2, 0, Math.PI * 2);
    ctx.stroke();

    // Audio-reactive ripples when speaking
    if (this.state === 'speaking' && amp > 0.12) {
      for (let i = 0; i < 3; i++) {
        const phase = (this.time * 0.6 + i * 0.33) % 1;
        const r = coreR + 8 + phase * baseR * 1.4;
        const a = (1 - phase) * 0.35 * (0.4 + amp);
        ctx.strokeStyle = withAlpha(pal.accent, a);
        ctx.lineWidth = 1.4;
        ctx.beginPath();
        ctx.arc(cx, cy, r, 0, Math.PI * 2);
        ctx.stroke();
      }
    }
  }

  private drawEnergyRing(
    cx: number,
    cy: number,
    r: number,
    thickness: number,
    rotation: number,
    color: string,
    intensity: number,
  ) {
    const { ctx } = this;
    const segments = 48;
    ctx.globalCompositeOperation = 'lighter';
    ctx.lineWidth = thickness;
    ctx.lineCap = 'round';
    for (let i = 0; i < segments; i++) {
      const a0 = (i / segments) * Math.PI * 2 + rotation;
      const a1 = a0 + (Math.PI * 2) / segments - 0.005;
      const noise = Math.sin(a0 * 5 + this.time * 2) * 0.5 + 0.5;
      const alpha = (0.12 + noise * 0.35) * (0.6 + intensity * 0.8);
      ctx.strokeStyle = withAlpha(color, alpha);
      ctx.beginPath();
      ctx.arc(cx, cy, r + noise * 3, a0, a1);
      ctx.stroke();
    }
  }
}

function withAlpha(rgba: string, a: number): string {
  const m = rgba.match(/rgba?\(([^)]+)\)/);
  if (!m) return rgba;
  const parts = (m[1] ?? '').split(',').map((v) => v.trim());
  return `rgba(${parts[0] ?? 255}, ${parts[1] ?? 255}, ${parts[2] ?? 255}, ${a.toFixed(3)})`;
}
